import { useMemo, useState } from 'react';

import { TableCell, Box, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';

import { HighnoteTransactionEvent } from 'API';
import { hideForMobile } from 'app/pages/overview/tabs/transactions/utils';
import { selectAgaveSlice } from 'app/pages/store/agaveSlice';
import { ERPFeatures, isErpFeatureEnabled } from 'app/services/agave/erpFeatureConfigs';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { parseDateAsLocal } from 'util/dateUtils';

export const checkAccountingDate = (date: string) => {
  try {
    const parsed = parseDateAsLocal(date);
    if (isNaN(parsed.getTime())) return date;
    return format(parsed, 'yyyy-MM-dd');
  } catch (error) {
    captureException(error);
    return date;
  }
};

export const useShowAccountingDateAppSettings = () => {
  // whether we display the accounting date setting in the app settings
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const { agaveAccount } = useAppSelector(selectAgaveSlice);

  return useMemo(() => {
    // if the accounting date setting exists, return true (makes the settings visible quicker)
    if (selectedPaidol?.accountingDateSettings) return true;

    // Prefer paidol field (instant), fallback to agaveAccount (async, for legacy clients)
    const erpSlug = selectedPaidol?.agaveSourceSystemSlug ?? agaveAccount?.source_system?.slug;

    // otherwise, check if the erp system has the feature enabled
    return isErpFeatureEnabled(erpSlug, ERPFeatures.accountingDate);
  }, [
    selectedPaidol?.agaveSourceSystemSlug,
    agaveAccount?.source_system?.slug,
    selectedPaidol?.accountingDateSettings,
  ]);
};

export const useAccountingDate = () => {
  // show the accounting date in transactions table/side-sheet, keep this seperate from the app settings since that requires fetching the account from agave to verify the erp system
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  return selectedPaidol?.accountingDateSettings?.isEnabled ?? false;
};

const useAccountingDatePicker = (
  transaction: HighnoteTransactionEvent,
  onDateChange: (date: string) => void
) => {
  // Return null when accountingDate is not set - show empty field with placeholder
  const dateValue = useMemo(
    () => (transaction.accountingDate ? parseDateAsLocal(transaction.accountingDate) : null),
    [transaction.accountingDate]
  );

  const handleDateChange = (newValue: Date | null) => {
    if (newValue) {
      const formattedDate = format(newValue, 'yyyy-MM-dd');
      onDateChange(formattedDate);
    }
  };

  return { dateValue, handleDateChange };
};

interface AccountingDateTableRowProps {
  transaction: HighnoteTransactionEvent;
  onDateChange: (date: string) => void;
}

/* Deprecated once agave transaction table is migrated to the new universal transactions table */
export function AccountingDateTableRow({ transaction, onDateChange }: AccountingDateTableRowProps) {
  const shouldShowAccountingDate = useAccountingDate();
  const { dateValue, handleDateChange } = useAccountingDatePicker(transaction, onDateChange);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const { t } = useTranslation();

  if (!shouldShowAccountingDate) return null;

  return (
    <TableCell
      sx={{ display: hideForMobile, minWidth: '180px' }}
      onClick={(e) => isDatePickerOpen && e.stopPropagation()}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          value={dateValue}
          open={isDatePickerOpen}
          onChange={(newValue) => {
            handleDateChange(newValue);
            setIsDatePickerOpen(false);
          }}
          onOpen={() => setIsDatePickerOpen(true)}
          onClose={() => setIsDatePickerOpen(false)}
          slotProps={{
            textField: {
              fullWidth: true,
              placeholder: t('selectDate'),
              onClick: (e) => e.stopPropagation(),
              InputProps: {
                disableUnderline: true,
              },
              sx: dateValue ? {} : { '& input': { color: 'text.secondary' } },
            },
          }}
        />
      </LocalizationProvider>
    </TableCell>
  );
}

interface AccountingDateSidesheetProps {
  transaction: HighnoteTransactionEvent;
  disabled?: boolean;
  enableHeader?: boolean;
  onDateChange: (date: string) => void;
}

export function AccountingDate({
  transaction,
  disabled,
  enableHeader = false,
  onDateChange,
}: AccountingDateSidesheetProps) {
  const shouldShowAccountingDate = useAccountingDate();

  const { t } = useTranslation();
  const { dateValue, handleDateChange } = useAccountingDatePicker(transaction, onDateChange);

  if (!shouldShowAccountingDate) return null;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: '150px' }}
      onClick={(e) => e.stopPropagation()}
    >
      {enableHeader && (
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          {t('accountingDate')}
        </Typography>
      )}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          value={dateValue}
          onChange={handleDateChange}
          disabled={disabled}
          slotProps={{
            textField: {
              fullWidth: true,
              placeholder: t('selectDate'),
              sx: dateValue ? {} : { '& input': { color: 'text.secondary' } },
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
}
